import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/store/auth.store'
import type { FeedResponse, PostType } from '@parchala/shared'

interface FeedOptions {
  type?: PostType
  category?: string
  zone?: string
  limit?: number
  offset?: number
}

export function useFeed(opts: FeedOptions = {}) {
  const params = new URLSearchParams()
  if (opts.type) params.set('type', opts.type)
  if (opts.category) params.set('category', opts.category)
  if (opts.zone) params.set('zone', opts.zone)
  params.set('limit', String(opts.limit ?? 20))
  params.set('offset', String(opts.offset ?? 0))

  return useQuery({
    queryKey: ['feed', opts],
    queryFn: () => api.get<FeedResponse>(`/api/feed?${params}`),
    staleTime: 1000 * 60,
  })
}

export function useNowFeed() {
  return useQuery({
    queryKey: ['feed', 'now'],
    queryFn: () => api.get<FeedResponse>('/api/feed/now'),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  })
}

/** IDs de los posts que el usuario logueado ya marcó como "me interesa". */
export function useMyInterests() {
  const user = useAuthStore((s) => s.user)

  const query = useQuery({
    queryKey: ['interests'],
    queryFn: () => api.get<{ postIds: string[] }>('/api/posts/interests'),
    enabled: !!user,
    staleTime: 1000 * 30,
  })

  return new Set(query.data?.postIds ?? [])
}

/** Posts guardados por el usuario logueado, con el negocio embebido. */
export function useSavedPosts() {
  const user = useAuthStore((s) => s.user)

  return useQuery({
    queryKey: ['saved'],
    queryFn: () => api.get<{ data: Record<string, unknown>[] }>('/api/posts/saved'),
    enabled: !!user,
    staleTime: 1000 * 30,
  })
}

/** Posts que le interesan al usuario logueado, con el negocio embebido. */
export function useInterestedPosts() {
  const user = useAuthStore((s) => s.user)

  return useQuery({
    queryKey: ['interested'],
    queryFn: () => api.get<{ data: Record<string, unknown>[] }>('/api/posts/interested'),
    enabled: !!user,
    staleTime: 1000 * 30,
  })
}
